import { FC, useState, useEffect } from 'react';
import { FileViewer } from 'react-file-viewer-v2';
import type { File as ApiFile } from '@teachur/http-client';
import { getFileUrl } from '@helpers';
import { getFileType } from '@utils/helpers';
import { Loader2 } from 'lucide-react';

interface ProjectFilePreviewPanelProps {
  file?: ApiFile | null;
  onClose: () => void;
}

const fileTypeMap: Record<string, string> = {
  pdf: 'pdf',
  word: 'docx',
  excel: 'xlsx',
  image: 'png',
  unknown: 'pdf',
};

export const ProjectFilePreviewPanel: FC<ProjectFilePreviewPanelProps> = ({ file }) => {
  const [fileBlob, setFileBlob] = useState<Blob | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  if (!file) return null;

  const url = getFileUrl(file.url);
  const fileType = getFileType(url);
  const mappedFileType = fileTypeMap[fileType] || fileTypeMap.unknown;

  useEffect(() => {
    const fetchFile = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        console.log('Fetched blob:', blob.type, blob.size);
        setFileBlob(blob);
      } catch (error) {
        console.error('Failed to load file:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFile();
  }, [url]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 text-(--neutral-black-NB400) animate-spin" />
      </div>
    );
  }

  if (!fileBlob) {
    return null;
  }

  return (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="w-full h-full min-h-[600px]">
        <FileViewer file={fileBlob} fileType={mappedFileType} theme="auto" />
      </div>
    </div>
  );
};
